/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from 'react-router-dom'
import { collection, onSnapshot, query, orderBy, limit, where } from 'firebase/firestore'
import { db } from '../../firebase';
import { Box, Link, Typography, useTheme, CircularProgress, Backdrop, ToggleButton, ToggleButtonGroup, Grid, useMediaQuery, Paper } from "@mui/material"
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import { tokens } from "../../theme"
import { IndividualStatusIcon } from "./lib/IndividualStatusIcon";

// 日付範囲の初期値を計算する関数
const getInitialDateRange = () => {
  const today = dayjs();
  const currentYear = today.year();
  const currentMonth = today.month(); // 0-11

  let startDate, endDate;

  // 1月から3月の場合は前年度
  if (currentMonth < 3) { // 0, 1, 2 = 1月, 2月, 3月
    startDate = dayjs().year(currentYear - 1).month(3).date(1); // 前年の4月1日
    endDate = dayjs().year(currentYear).month(2).date(31); // 当年の3月31日
  } else {
    startDate = dayjs().year(currentYear).month(3).date(1); // 当年の4月1日
    endDate = dayjs().year(currentYear + 1).month(2).date(31); // 翌年の3月31日
  }

  return { startDate, endDate };
};

// ステータスの選択肢
const statusOptions = [
  { value: "accepted", label: "受付済" },
  { value: "inspected", label: "検査済" },
  { value: "dismantled", label: "解体済" },
  { value: "processed", label: "加工済" },
  { value: "shipped", label: "出荷済" }
];

const IndividualList = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const colors = tokens(theme.palette.mode)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [items, setItems] = useState<Array<object>>([])
  // sessionStorageからステータスフィルターを取得、なければデフォルト値を使用
  const defaultStatusFilter = ["accepted", "inspected", "dismantled", "processed", "shipped"];
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const savedFilter = sessionStorage.getItem('individualListStatusFilter');
    return savedFilter ? JSON.parse(savedFilter) : defaultStatusFilter;
  });

  // 日付範囲フィルターの初期値を設定
  const [dateRange, setDateRange] = useState(() => {
    const savedDateRange = sessionStorage.getItem('individualListDateRange');
    if (savedDateRange) {
      const parsed = JSON.parse(savedDateRange);
      return {
        startDate: parsed.startDate ? dayjs(parsed.startDate) : null,
        endDate: parsed.endDate ? dayjs(parsed.endDate) : null
      };
    }
    return getInitialDateRange();
  });

  const [loading, setLoading] = useState<boolean>(true)
  const individualsRef = collection(db, 'individuals')

  // 合計体重を計算（kg単位）
  const totalWeight = useMemo(() => {
    return items.reduce((sum: number, item: any) => {
      const weight = typeof item.weight === 'string'
        ? parseFloat(item.weight) || 0
        : (typeof item.weight === 'number' ? item.weight : 0)
      return sum + weight
    }, 0)
  }, [items])

  const handleStatusChange = (
    _event: React.MouseEvent<HTMLElement>,
    newStatuses: string[],
  ) => {
    // 少なくとも1つのステータスが選択されていることを確認
    if (newStatuses.length > 0) {
      setStatusFilter(newStatuses);
      // sessionStorageに保存
      sessionStorage.setItem('individualListStatusFilter', JSON.stringify(newStatuses));
    }
  };

  // 開始日の変更ハンドラー
  const handleStartDateChange = (newDate: dayjs.Dayjs | null) => {
    const newDateRange = { ...dateRange, startDate: newDate };
    setDateRange(newDateRange);
    // sessionStorageに保存
    sessionStorage.setItem('individualListDateRange', JSON.stringify({
      startDate: newDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString()
    }));
  };

  // 終了日の変更ハンドラー
  const handleEndDateChange = (newDate: dayjs.Dayjs | null) => {
    const newDateRange = { ...dateRange, endDate: newDate };
    setDateRange(newDateRange);
    // sessionStorageに保存
    sessionStorage.setItem('individualListDateRange', JSON.stringify({
      startDate: dateRange.startDate?.toISOString(),
      endDate: newDate?.toISOString()
    }));
  };
  const columns = isMobile ? [
    {
      field: "No",
      headerName: "No",
      width: 50,
      sortComparator: gridNumberComparator,
    },
    {
      field: "name",
      headerName: "搬入者",
      width: 100,
      cellClassName: "name-column--cell",
    },
    {
      field: "arrivalDatetime",
      headerName: "搬入日時",
      width: 150,
    },
    {
      field: "detail",
      headerName: "詳細",
      width: 80,
      renderCell: (params: any) => (
        <Link color="secondary" style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/individuals/${params.row.id}`)}><IndividualStatusIcon status={params.row.status} size="small" /></Link>
      ),
    }
  ] : [
    {
      field: "No",
      headerName: "Mo",
      width: 10,
      sortComparator: gridNumberComparator,
    },
    {
      field: "name",
      headerName: "搬入者名",
      width: 200,
      cellClassName: "name-column--cell",
    },
    {
      field: "arrivalDatetime",
      headerName: "搬入日時",
      flex: 1,
    },
    {
      field: "sex",
      headerName: "性別",
      flex: 1,
    },
    {
      field: "age",
      headerName: "推定年齢",
      width: 100,
    },
    {
      field: "weight",
      headerName: "体重",
      width: 100,
      valueFormatter: (params: any) => {
        return params.value ? `${params.value} kg` : '';
      },
    },
    {
      field: "detail",
      headerName: "詳細",
      flex: 1,
      renderCell: (params: any) => (
        <Link color="secondary" style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/individuals/${params.row.id}`)}><IndividualStatusIcon status={params.row.status} size="small" /></Link>
      ),
    }
  ]

  useEffect(() => {
    if (!individualsRef) return

    let q;

    // ステータスフィルター条件
    let baseQuery;
    if (statusFilter.length === statusOptions.length) {
      // すべてのステータスが選択されている場合
      baseQuery = query(
        individualsRef,
        where("status", "not-in", ["init", "registed"])
      );
    } else {
      // 選択されたステータスのみ
      baseQuery = query(
        individualsRef,
        where("status", "in", statusFilter)
      );
    }

    // 日付範囲フィルター条件を追加
    if (dateRange.startDate && dateRange.endDate) {
      // 開始日と終了日の両方が設定されている場合
      const startDateStr = dateRange.startDate.format('YYYY-MM-DD');
      // 終了日は次の日の0時0分0秒の直前までを含める
      const nextDayStr = dateRange.endDate.add(1, 'day').format('YYYY-MM-DD');

      q = query(
        baseQuery,
        where("receptionDatetime", ">=", startDateStr),
        where("receptionDatetime", "<", nextDayStr),
        orderBy("receptionDatetime", "desc"),
        limit(50)
      );
    } else if (dateRange.startDate) {
      // 開始日のみ設定されている場合
      const startDateStr = dateRange.startDate.format('YYYY-MM-DD');
      q = query(
        baseQuery,
        where("receptionDatetime", ">=", startDateStr),
        orderBy("receptionDatetime", "desc"),
        limit(50)
      );
    } else if (dateRange.endDate) {
      // 終了日のみ設定されている場合
      // 終了日は次の日の0時0分0秒の直前までを含める
      const nextDayStr = dateRange.endDate.add(1, 'day').format('YYYY-MM-DD');

      q = query(
        baseQuery,
        where("receptionDatetime", "<", nextDayStr),
        orderBy("receptionDatetime", "desc"),
        limit(50)
      );
    } else {
      // 日付範囲が設定されていない場合
      q = query(
        baseQuery,
        orderBy("receptionDatetime", "desc"),
        limit(50)
      );
    }

    setLoading(true); // クエリ実行時にローディング表示

    // 10秒後に強制的にローディング表示を無効化（タイムアウト処理）
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 10000);

    const unsubscribe = onSnapshot(
      q,
      ({ docs }) => {
        setItems(docs.map((_, i) => {
          return ({ No: i + 1, ..._.data(), id: _.id })
        }))
        setLoading(false); // データ取得完了時にローディング非表示
      },
      (error) => {
        console.error("Error fetching data:", error);
        setLoading(false); // エラー発生時もローディング非表示
      }
    )
    return () => {
      unsubscribe();
      clearTimeout(timeoutId); // タイムアウトをクリア
      setLoading(false); // クリーンアップ時にもローディング非表示
    }
  }, [statusFilter, dateRange]) // statusFilterまたはdateRangeが変更されたときにも再実行

  return (
    <Box m={isMobile ? "10px" : "20px"}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant={isMobile ? "h4" : "h3"}>受入個体一覧</Typography>

        <Grid container spacing={2} alignItems="center">
          {/* 日付範囲フィルター */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>期間</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
              <Box display="flex" alignItems="center" gap={1}>
                <DatePicker
                  label="開始日"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <Typography>～</Typography>
                <DatePicker
                  label="終了日"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>
          </Grid>

          {/* ステータスフィルター */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>ステータスフィルター</Typography>
            <ToggleButtonGroup
              value={statusFilter}
              onChange={handleStatusChange}
              aria-label="ステータスフィルター"
              size="small"
              exclusive={false}
              sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': {
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  padding: isMobile ? '4px 8px' : '6px 12px',
                }
              }}
            >
              {statusOptions.map((option) => (
                <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Box>
      {/* 集計表示 */}
      <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
        <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
          <Typography variant="h6" color="primary">
            件数: {items.length}件
          </Typography>
          <Typography variant="h6" color="secondary">
            合計体重: {totalWeight.toFixed(1)} kg
          </Typography>
        </Box>
      </Paper>

      {/* ローディングオーバーレイ */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box
        m={isMobile ? 1 : 2}
        height={isMobile ? "70vh" : "80vh"}
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={items}
          columns={columns}
          loading={loading}
        />
      </Box>
    </Box>
  )
}
export default IndividualList
